/*
 *  Remember to wrap the module with Router from 'react-router' to be able to use this hook
 */
import isEmpty from 'lodash/isEmpty'
import pick from 'lodash/pick'
import { IParseOptions, IStringifyOptions, parse, stringify } from 'qs'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  type Location,
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
  useSearchParams,
} from 'react-router'

import { fixSearchParamsArrays } from '../utils/fixSearchParamsArrays'

// The v6 `Navigator` type doesn't declare `location`, but every navigator we mount has one:
// HistoryRouter passes a real history@5 object through, and v6's own BrowserRouter/HashRouter/
// MemoryRouter expose it too
const isLocation = (value: unknown): value is Pick<Location, 'pathname' | 'search'> =>
  typeof value === 'object' &&
  value !== null &&
  'pathname' in value &&
  typeof value.pathname === 'string' &&
  'search' in value &&
  typeof value.search === 'string'

const parseOptions: IParseOptions = {
  allowDots: true,
  ignoreQueryPrefix: true,
}

const stringifyOptions: IStringifyOptions = {
  allowDots: true,
}

export type UseQueryParamsResponse<T> = [
  query: T,
  setQuery: (params: Partial<T> | ((prevParams: Partial<T>) => Partial<T>)) => void,
]

interface UseQueryParams<T> {
  defaultQuery: T
  enabled?: boolean
  // Use a unique key if you want to use queries more than once in a single view (e.g. two different tables)
  key?: string
  // It sets default params in URL
  withDefaultQueryInUrl?: boolean
}

interface ParsedSearch<T> {
  urlQuery: Record<string, any>
  urlQuerySpecified: Partial<T>
  urlQueryAllowed: Partial<T>
}

// Splits a raw search string into the three slices. Read handlers use `urlQueryAllowed`;
// Write handlers rebuild the URL from all three
//
// Examples used in the comments below, both with defaultQuery: { page: 1, status: 'all' }:
// - no key (the common case): URL "?page=2&foo=x"
// - namespaced: URL "?mit.page=2&mit.foo=x&mrt.page=5", read by a hook created with key: 'mit'
const parseSearch = <T extends Record<string, any>>(search: string, defaultQuery: T, key?: string): ParsedSearch<T> => {
  // The whole parsed search string, including params that are not ours:
  // no key: { page: '2', foo: 'x' }
  // namespaced: { mit: { page: '2', foo: 'x' }, mrt: { page: '5' } }
  const urlQuery = parse(search, parseOptions)
  // "Specified" = the slice under this hook's `key`, or the whole query when no key is used,
  // in both examples: { page: '2', foo: 'x' }
  const urlQuerySpecified = (key ? urlQuery[key] : urlQuery) as Partial<T>
  // "Allowed" = urlQuerySpecified restricted to the keys declared in defaultQuery
  // { page: '2' }, 'foo' is dropped because it is not a defaultQuery key
  const urlQueryAllowed = pick(urlQuerySpecified, Object.keys(defaultQuery)) as Partial<T>

  return { urlQuery, urlQuerySpecified, urlQueryAllowed }
}

export const useQueryParams = <T extends Record<string, any>>(props: UseQueryParams<T>): UseQueryParamsResponse<T> => {
  const { defaultQuery, enabled = true, key, withDefaultQueryInUrl = true } = props || {}
  const [searchParams] = useSearchParams()
  const { navigator } = useContext(NavigationContext)
  const renderLocation = useLocation()

  // The main and correct query: the current URL params (filtered to align with the keys in
  // defaultQuery) + other defaults if missing. It is taken from the URL on every URL change.
  //
  // `defaultQuery` is deliberately not a dependency here: callers rebuild it inline on every render
  // (memoised JS object), so depending on it would cause reupdates and rerenders.
  const urlBackedQuery = useMemo(() => {
    const { urlQueryAllowed } = parseSearch(searchParams.toString(), defaultQuery, key)

    return { ...defaultQuery, ...urlQueryAllowed }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, searchParams])

  // Latest values for the setter below, so it can keep a stable identity without going stale
  const defaultQueryRef = useRef(defaultQuery)
  defaultQueryRef.current = defaultQuery
  const renderLocationRef = useRef(renderLocation)
  renderLocationRef.current = renderLocation

  // Main query setter: merges the given params over the current query and replaces the URL with the
  // result. Only the keys declared in defaultQuery are touched and `undefined` values are dropped,
  // so setQuery({ status: 'draft' }) turns "?page=2&foo=x" into "?page=2&foo=x&status=draft":
  // 'page' keeps its URL value and 'foo' survives because it is not one of ours. urlBackedQuery is
  // then rebuilt from the new URL.
  //
  // The current query is read from the navigator's live location rather than from this render's
  // `searchParams`, so two setQuery calls before the next render (e.g. both in the same event
  // handler) see each other instead of both starting from the same stale search string.
  //
  // The write passes a full pathname + search instead of calling `setSearchParams('?...')`. A
  // relative '?...' is resolved against the pathname of the last render, so a write that happens
  // before React has re-rendered after a navigation (e.g. a <Navigate> on mount) would put the
  // old pathname back and silently undo it.
  const setUrlBackedParams = useCallback(
    (params: Partial<T> | ((prevParams: Partial<T>) => Partial<T>)) => {
      const defaultQuery = defaultQueryRef.current
      // History object's current location (already updated by earlier writes / navigations);
      // or last render's location if this navigator doesn't expose one
      const { pathname, search } =
        'location' in navigator && isLocation(navigator.location) ? navigator.location : renderLocationRef.current
      const { urlQuery, urlQuerySpecified, urlQueryAllowed } = parseSearch(search, defaultQuery, key)

      const prevQuery = { ...defaultQuery, ...urlQueryAllowed }
      const nextQuery = typeof params === 'function' ? params(prevQuery) : params
      const updatedQuery = { ...prevQuery, ...nextQuery }

      const searchLocationMerged = {
        ...urlQuery,
        ...(key
          ? {
              [key]: { ...urlQuerySpecified, ...updatedQuery },
            }
          : updatedQuery),
      }

      const nextSearch = stringify(fixSearchParamsArrays(searchLocationMerged), stringifyOptions)
      const nextSearchWithPrefix = nextSearch ? `?${nextSearch}` : ''

      // Nothing to change: skip the replace so a consumer that sets params in an effect can't loop
      if (nextSearchWithPrefix === search) {
        return
      }

      navigator.replace({ pathname, search: nextSearchWithPrefix, hash: '' })
    },
    [key, navigator],
  )

  // The query/setter pair returned while NOT `enabled` (e.g. a list embedded in a modal that must
  // not pollute the page URL): plain component state, never read from or written to the URL.
  const [localQuery, setLocalQuery] = useState<T>(defaultQuery)

  const setLocalParams = useCallback((params: Partial<T> | ((prevParams: Partial<T>) => Partial<T>)) => {
    setLocalQuery((prevQuery) => ({
      ...prevQuery,
      ...(typeof params === 'function' ? params(prevQuery) : params),
    }))
  }, [])

  // Puts the default query into the URL when the URL has none of our params yet
  // (withDefaultQueryInUrl). It is a write, not a read, so it cannot be derived — it stays an
  // effect. It runs on mount (and re-enable) only
  useEffect(() => {
    if (
      !enabled ||
      !withDefaultQueryInUrl ||
      !isEmpty(parseSearch(searchParams.toString(), defaultQuery, key).urlQueryAllowed)
    ) {
      return
    }

    setUrlBackedParams((prevQuery) => ({ ...defaultQuery, ...prevQuery }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  return enabled ? [urlBackedQuery, setUrlBackedParams] : [localQuery, setLocalParams]
}
